import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import Alert from "../../components/Alert";
import { createLookupApi } from "../../api/lookupApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { listSubHeadConfigs, individualAssignRoster, saveIndividualAssignBulk } from "../../api/feeApi";
import "../Attendance/Attendance.css";

const academicYearApi = createLookupApi("academic-years");
const feeHeadApi = createLookupApi("fee-heads");

/**
 * Direct port of digicampusbd's real `student-individual-fee-asign`
 * ("Individual Fee Assign") — a bulk, roster-driven fixed-amount override:
 * pick one fee head and one or more of its sub-heads, search a section's
 * roster (each row pre-filled with that student's existing override amount,
 * 0 if none), check the students to adjust, edit each one's amount, and
 * save — the SAME amount is stamped onto every selected sub-head for that
 * student. Confirmed via the old app's own real template
 * (`student-individual-fee-asign.html`) and
 * `StudentIndividualFeeAssignService.saveStudentIndividualFeeAssignMultiSubHead`.
 *
 * Documented simplification: the real old app can also search by whole
 * CLASS (every section at once) via a separate radio option; this port
 * only searches by one section (`classConfigId`) at a time, matching how
 * every other bulk search in this migration works.
 */
export default function IndividualFeeAssignPage() {
  const [academicYears, setAcademicYears] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [classConfigs, setClassConfigs] = useState([]);
  const [classConfigId, setClassConfigId] = useState("");
  const [feeHeads, setFeeHeads] = useState([]);
  const [feeHeadId, setFeeHeadId] = useState("");
  const [feeSubHeads, setFeeSubHeads] = useState([]);
  const [selectedSubHeadIds, setSelectedSubHeadIds] = useState([]);

  const [rows, setRows] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [amounts, setAmounts] = useState({});
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    academicYearApi.list().then((years) => {
      setAcademicYears(years);
      if (years.length > 0) setAcademicYear(years[0].name);
    });
    listClassConfigurations().then((configs) => {
      setClassConfigs(configs);
      if (configs.length > 0) setClassConfigId(String(configs[0].id));
    });
    feeHeadApi.list().then(setFeeHeads);
  }, []);

  useEffect(() => {
    if (!feeHeadId) {
      setFeeSubHeads([]);
      return;
    }
    listSubHeadConfigs(feeHeadId).then((data) => setFeeSubHeads(data.map((r) => ({ id: r.feeSubHeadId, name: r.feeSubHeadName }))));
    setSelectedSubHeadIds([]);
  }, [feeHeadId]);

  const toggleSubHead = (id) => setSelectedSubHeadIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleSearch = async () => {
    setError("");
    setSuccess("");
    if (!feeHeadId || selectedSubHeadIds.length === 0 || !classConfigId || !academicYear) {
      setError("Please select a year, section, fee head, and at least one sub-head.");
      return;
    }
    setSearching(true);
    try {
      const data = await individualAssignRoster(feeHeadId, selectedSubHeadIds.map(Number), classConfigId, academicYear);
      setRows(data);
      setSelectedIds([]);
      const initialAmounts = {};
      data.forEach((r) => (initialAmounts[r.identificationId] = r.amount || 0));
      setAmounts(initialAmounts);
    } catch (err) {
      setError("Failed to load students.");
    } finally {
      setSearching(false);
    }
  };

  const toggleStudent = (id) => setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleAll = () =>
    setSelectedIds((prev) => (rows && prev.length === rows.length ? [] : (rows || []).map((r) => r.identificationId)));

  const handleSave = async () => {
    setError("");
    setSuccess("");
    if (selectedIds.length === 0) {
      setError("Please select at least one student.");
      return;
    }
    try {
      await saveIndividualAssignBulk({
        feeHeadId: Number(feeHeadId),
        feeSubHeadIds: selectedSubHeadIds.map(Number),
        studentInfos: selectedIds.map((id) => ({ identificationId: id, feeAmount: Number(amounts[id]) || 0 })),
      });
      setSuccess(`Saved for ${selectedIds.length} student(s).`);
      handleSearch();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to save.");
    }
  };

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Individual Fee Assign</h1>
      </div>
      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      <div className="attendance-filters">
        <label>
          Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Section
          <select value={classConfigId} onChange={(e) => setClassConfigId(e.target.value)}>
            {classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Fee Head
          <select value={feeHeadId} onChange={(e) => setFeeHeadId(e.target.value)} required>
            <option value="" disabled>
              Select
            </option>
            {feeHeads.map((h) => (
              <option key={h.id} value={h.id}>
                {h.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div style={{ margin: "8px 0 16px" }}>
        <span className="admit-card-label">Fee Sub-Head(s):</span>{" "}
        {feeSubHeads.map((s) => (
          <label key={s.id} className="checkbox-inline" style={{ display: "inline-flex", marginRight: 12 }}>
            <input type="checkbox" checked={selectedSubHeadIds.includes(String(s.id))} onChange={() => toggleSubHead(String(s.id))} />
            {s.name}
          </label>
        ))}
        <button type="button" className="btn-primary" onClick={handleSearch} disabled={searching} style={{ marginLeft: 12 }}>
          {searching ? "Searching..." : "Search"}
        </button>
      </div>

      {rows && (
        <>
          <div className="table-scroll">
            <table className="attendance-table">
              <thead>
                <tr>
                  <th>
                    <input type="checkbox" checked={rows.length > 0 && selectedIds.length === rows.length} onChange={toggleAll} />
                  </th>
                  <th>Student ID</th>
                  <th>Name</th>
                  <th>Roll</th>
                  <th>Fixed Fee Amount</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.identificationId}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(r.identificationId)}
                        onChange={() => toggleStudent(r.identificationId)}
                      />
                    </td>
                    <td>{r.customStudentId}</td>
                    <td>{r.studentName}</td>
                    <td>{r.studentRoll}</td>
                    <td>
                      <input
                        type="number"
                        style={{ width: 100 }}
                        value={amounts[r.identificationId] ?? 0}
                        onChange={(e) => setAmounts((prev) => ({ ...prev, [r.identificationId]: e.target.value }))}
                      />
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={5}>No students found for this section.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="form-actions">
            <button type="button" className="btn-primary" onClick={handleSave}>
              Save
            </button>
          </div>
        </>
      )}
    </MainLayout>
  );
}
